/**
 * NamedWindowRefNote — the `OVER w` reference note shown inside the expanded
 * inline-OVER editor when a window function references a named window
 * (`func() OVER w`). Task §4b-ii (BUG 3b): replaces the old static "References
 * named window w defined in the WINDOW clause" text with a real control that
 *  1. LINKS to the definition — clicking scrolls the matching `WINDOW w AS (…)`
 *     card into view and briefly highlights it (fires `feature:vqe_window_ref_focus`);
 *  2. shows a RESOLVED read-back — a compact `PARTITION BY … · ORDER BY …`
 *     summary of the referenced definition's spec — so the driving window is
 *     visible at the reference site without scrolling.
 *
 * The read-back is derived from the resolver's already-parsed
 * `QueryStructure.windows` (byte-identical to an inline OVER spec — §4b-i), so
 * it needs no buffer re-slice. Available whenever an `editContext` is present
 * (a pure parse — no schema snapshot required); otherwise it degrades to the
 * plain linked note.
 */
import { useMemo } from 'react';

import { useAnalytics } from '../../../../../core/analytics/useAnalytics';
import type { ParseOptions } from '../../../../sql-grammar/model';
import type { VqeEditContext } from '../../edit/editContext';
import { extractStructureForContext } from '../../edit/editContext';
import type { WindowSpec } from '../../../../sql-resolver/types';
import { vqeCopy } from '../../ux/vqeCopy';
import { VqeTooltip } from '../common/VqeTooltip';

export interface NamedWindowRefNoteProps {
  readonly name: string;
  readonly options: ParseOptions;
  readonly testIdPrefix: string;
  readonly editContext?: VqeEditContext;
}

type ReadBack = { readonly missing: boolean; readonly summary: string };

function summarizeWindowSpec(spec: WindowSpec): string {
  const parts: string[] = [];
  const partition = (spec.partitionBy ?? []).map((r) => r.qualified.name).filter(Boolean);
  if (partition.length > 0) parts.push(`PARTITION BY ${partition.join(', ')}`);
  const order = (spec.orderBy ?? []).map((o) => {
    const dir = o.direction === 'desc' ? ' DESC' : o.direction === 'asc' ? ' ASC' : '';
    return `${o.expressionText}${dir}`;
  }).filter((s) => s.trim().length > 0);
  if (order.length > 0) parts.push(`ORDER BY ${order.join(', ')}`);
  if (spec.frame) parts.push(spec.frame.text);
  return parts.join(' · ');
}

export function NamedWindowRefNote({ name, options, testIdPrefix, editContext }: NamedWindowRefNoteProps) {
  const { track } = useAnalytics();

  const readBack = useMemo<ReadBack | null>(() => {
    if (!editContext) return null;
    try {
      const structure = extractStructureForContext(editContext);
      const target = (structure.windows ?? []).find(
        (d) => d.name.toLowerCase() === name.toLowerCase(),
      );
      if (!target) return { missing: true, summary: '' };
      return { missing: false, summary: summarizeWindowSpec(target.spec) };
    } catch {
      return null;
    }
  }, [editContext, name]);

  const focusDefinition = (): void => {
    track('feature:vqe_window_ref_focus', { engine: options.engine });
    if (typeof document === 'undefined') return;
    const target = document.querySelector<HTMLElement>(`[data-window-name="${name.toLowerCase()}"]`);
    if (!target) return;
    target.scrollIntoView({ block: 'nearest' });
    target.setAttribute('data-window-ref-focus', 'true');
    window.setTimeout(() => target.removeAttribute('data-window-ref-focus'), 1200);
  };

  const readBackText = readBack === null
    ? null
    : readBack.missing
      ? vqeCopy.window.named.referenceMissing
      : readBack.summary.length > 0
        ? readBack.summary
        : vqeCopy.window.named.referenceNoSpec;

  return (
    <span className="flex flex-wrap items-center gap-1.5 text-[11px] text-muted-foreground" data-testid={`${testIdPrefix}-named-hint`}>
      <span>References named window</span>
      <VqeTooltip content={vqeCopy.window.named.referenceFocus}>
        <button
          type="button"
          onClick={focusDefinition}
          data-testid={`${testIdPrefix}-named-focus`}
          className="rounded bg-background px-1.5 py-0.5 font-mono text-[11px] underline decoration-dotted hover:decoration-solid"
        >
          {name}
        </button>
      </VqeTooltip>
      {readBackText ? (
        <span className="font-mono text-[11px] text-muted-foreground/80" data-testid={`${testIdPrefix}-named-readback`}>
          · {readBackText}
        </span>
      ) : null}
    </span>
  );
}
